import { useEffect } from 'react';
import { ErreurApi } from '../api/client';
import { useAttendreReglement } from '../api/hooks';
import { euros, formatCourt } from '../format';
import type { CommandeListee } from '../api/types';
import { useToasts } from './Toasts';

/**
 * Avertissement avant de passer au fournisseur une commande dont la facture
 * n'est pas réglée.
 *
 * La commande figure dans « À faire » comme les autres : c'est là qu'on vient
 * la passer, et l'en sortir la faisait oublier. Mais la passer engage une
 * dépense chez le fournisseur avant d'avoir été payé — on avance l'argent.
 * Le geste reste donc possible, à condition de le vouloir : cette fenêtre le
 * dit, et ne mène à la préparation du mail que sur confirmation.
 *
 * Confirmer n'envoie rien : la fenêtre de relecture s'ouvre ensuite, comme
 * pour n'importe quelle commande.
 *
 * Troisième réponse, à côté d'« Annuler » et de « Continuer » : ATTENDRE le
 * règlement. C'est souvent la vraie décision devant cette fenêtre, et elle se
 * prend ici — la commande passe dans « En cours » et revient d'elle-même dans
 * « À faire » quand Sellsy la dit réglée.
 */
export function ConfirmerFactureNonReglee({
  commande, raison, onConfirmer, onFermer,
}: {
  commande: CommandeListee;
  /** Le `blocage` de l'action, tel que le moteur l'a formulé. */
  raison: string;
  onConfirmer: () => void;
  onFermer: () => void;
}) {
  const attendre = useAttendreReglement();
  const { ajouter } = useToasts();
  // Déjà en attente — on passe la commande malgré tout : proposer d'attendre
  // n'aurait aucun effet.
  const dejaEnAttente = Boolean(commande.attenteReglement);

  async function attendreLeReglement() {
    try {
      await attendre.mutateAsync({ id: commande.id, attendre: true });
      ajouter(`${commande.ref} attend son règlement : elle passe dans « En cours » jusqu’au paiement.`);
      onFermer();
    } catch (e) {
      ajouter(e instanceof ErreurApi ? e.message : 'L’attente n’a pas pu être posée.', 'bad');
    }
  }

  useEffect(() => {
    const auClavier = (e: KeyboardEvent) => { if (e.key === 'Escape') onFermer(); };
    document.addEventListener('keydown', auClavier);
    return () => document.removeEventListener('keydown', auClavier);
  }, [onFermer]);

  return (
    <div className="modal" onClick={(e) => { if (e.target === e.currentTarget) onFermer(); }}>
      <div
        className="box" role="dialog" aria-modal="true"
        aria-label={`Facture non réglée pour ${commande.ref} : continuer ?`}
        style={{ borderTopColor: 'var(--brick)' }}
      >
        <div className="mhead">
          <h2>Facture non réglée</h2>
          <button className="x" onClick={onFermer} aria-label="Fermer">&times;</button>
        </div>

        <div className="mbody">
          <p className="err" style={{ marginBottom: 14 }}>
            La facture de cette commande <strong>n&apos;est pas réglée</strong>. La passer au
            fournisseur maintenant, c&apos;est avancer l&apos;argent de la marchandise.
          </p>

          <dl className="kv" style={{ marginBottom: 14 }}>
            <dt>Commande</dt><dd style={{ textAlign: 'left' }}>{commande.ref}</dd>
            <dt>Client</dt><dd style={{ textAlign: 'left' }}>{commande.client}</dd>
            <dt>Montant</dt><dd>{euros(commande.montant)}</dd>
            <dt>Reçue le</dt><dd>{formatCourt(commande.dateReception)}</dd>
          </dl>

          <p style={{ fontSize: 11.5, color: 'var(--ink-3)', margin: 0 }}>
            {raison} Le mail au fournisseur s&apos;ouvrira ensuite pour relecture : rien ne part
            sans que vous l&apos;ayez validé.
          </p>
        </div>

        <div className="mfoot">
          <button type="button" className="btn" onClick={onFermer}>Annuler</button>
          {!dejaEnAttente && (
            <button
              type="button" className="btn" disabled={attendre.isPending}
              title="La commande passe dans « En cours » et revient dans « À faire » dès que Sellsy la dit réglée."
              onClick={() => void attendreLeReglement()}
            >
              {attendre.isPending ? <><span className="spin" /> …</> : 'Attendre le règlement'}
            </button>
          )}
          <button type="button" className="btn pri" onClick={onConfirmer}>
            Continuer quand même
          </button>
        </div>
      </div>
    </div>
  );
}
